<c-container fluid class="p-4">
  <c-row>
    <c-col xs="12">
      <!-- Disabled orders banner -->
      @if (ordersDisabled()) {
        <c-alert color="info" class="mb-4">
          <div class="d-flex align-items-center">
            <svg cIcon name="cilInfo" class="me-2" size="xl"></svg>
            <div>
              <strong>{{ 'view_only_mode' | translate }}</strong>
              <p class="mb-0 mt-1">
                {{ 'order_view_only_preview' | translate }}
              </p>
            </div>
          </div>
        </c-alert>
      }

      <c-card class="mb-4">
        <c-card-header class="d-flex justify-content-between align-items-center">
          <h6 class="mb-0">
            {{ 'my_orders_total' | translate }}: {{ totalCount() }} {{ 'my_orders_orders' | translate }}
          </h6>

          <button
            cButton
            color="primary"
            size="sm"
            (click)="loadOrders()"
            [disabled]="loading()"
          >
            <svg cIcon name="cilReload" size="sm"></svg>
            <span class="ms-1">{{ 'my_orders_refresh' | translate }}</span>
          </button>
        </c-card-header>

        <c-card-body>
          <!-- Loading -->
          @if (loading()) {
            <div class="text-center py-5">
              <c-spinner color="primary" variant="grow"></c-spinner>
              <p class="mt-3 text-body-secondary">{{ 'my_orders_loading_message' | translate }}</p>
            </div>
          }

          <!-- Empty state -->
          @if (!loading() && orders().length === 0) {
            <div class="text-center py-5">
              <svg cIcon name="cilCart" size="4xl" class="text-body-secondary mb-3"></svg>
              <h5 class="text-body-secondary">{{ 'my_orders_empty_title' | translate }}</h5>
              <p class="text-body-secondary">{{ 'my_orders_empty_message' | translate }}</p>
            </div>
          }

          <!-- Orders table -->
          @if (!loading() && orders().length > 0) {
            <div class="table-responsive">
              <table cTable hover striped class="mb-0">
                <thead>
                  <tr>
                    <th>{{ 'my_orders_order_id' | translate }}</th>
                    <th>{{ 'my_orders_order_date' | translate }}</th>
                    <th>{{ 'my_orders_payment_method' | translate }}</th>
                    <th>{{ 'my_orders_status' | translate }}</th>
                    <th class="text-end">{{ 'my_orders_total' | translate }}</th>
                    <th class="text-center">{{ 'my_orders_items' | translate }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  @for (order of orders(); track order.id) {
                    <tr
                      class="cursor-pointer"
                      (click)="openOrderDetails(order.id)"
                    >
                      <td>
                        <span class="fw-bold">#{{ order.id }}</span>
                      </td>
                      <td>{{ order.created_at }}</td>
                      <td>{{ order.payment_method }}</td>
                      <td>
                        <c-badge [color]="getStatusBadgeColor(order.status)">
                          {{ order.status }}
                        </c-badge>
                      </td>
                      <td class="text-end">{{ order.total_price }}</td>
                      <td class="text-center">{{ order.items_count }}</td>
                      <td class="text-end">
                        <button
                          cButton
                          color="link"
                          class="p-0"
                          (click)="$event.stopPropagation(); openOrderDetails(order.id)"
                          [title]="'my_orders_view_details' | translate"
                        >
                          <svg cIcon name="cilInfo"></svg>
                        </button>
                        <button
                          cButton
                          color="link"
                          class="p-0 ms-2"
                          (click)="$event.stopPropagation(); downloadBill(order.id)"
                          [title]="'my_orders_download_bill' | translate"
                        >
                          <svg cIcon name="cilCloudDownload"></svg>
                        </button>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            @if (totalPages() > 1) {
              <div class="p-3 border-top">
                <c-row class="align-items-center">
                  <c-col xs="12" md="6">
                    <div class="text-body-secondary small">
                      {{ 'my_orders_showing' | translate }}
                      {{ (currentPage() - 1) * itemsPerPage() + 1 }} –
                      {{ Math.min(currentPage() * itemsPerPage(), totalCount()) }}
                      {{ 'my_orders_of' | translate }} {{ totalCount() }}
                      {{ 'my_orders_orders' | translate }}
                    </div>
                  </c-col>

                  <c-col xs="12" md="6">
                    <nav>
                      <ul class="pagination pagination-sm justify-content-end mb-0">
                        <li class="page-item" [class.disabled]="currentPage() === 1">
                          <button
                            class="page-link"
                            (click)="onActivePageChange(currentPage() - 1)"
                            [disabled]="currentPage() === 1"
                          >
                            &laquo;
                          </button>
                        </li>

                        @for (page of pageNumbers(); track page) {
                          <li class="page-item" [class.active]="page === currentPage()">
                            <button class="page-link" (click)="onActivePageChange(page)">
                              {{ page }}
                            </button>
                          </li>
                        }

                        <li class="page-item" [class.disabled]="currentPage() === totalPages()">
                          <button
                            class="page-link"
                            (click)="onActivePageChange(currentPage() + 1)"
                            [disabled]="currentPage() === totalPages()"
                          >
                            &raquo;
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </c-col>
                </c-row>
              </div>
            }
          }
        </c-card-body>
      </c-card>
    </c-col>
  </c-row>

  <!-- Order Details Modal -->
  <c-modal
    [visible]="detailsModalVisible()"
    (visibleChange)="detailsModalVisible.set($event)"
    backdrop="static"
    size="xl"
    scrollable="true"
  >
    <c-modal-header class="d-flex justify-content-between align-items-center">
      <h5 cModalTitle class="mb-0">
        {{ 'my_orders_order' | translate }} #{{ selectedOrder()?.id }} {{ 'my_orders_details' | translate }}
      </h5>
      <button
        type="button"
        cButtonClose
        [attr.aria-label]="'my_orders_close' | translate"
        (click)="closeOrderDetails()"
      >
      </button>
    </c-modal-header>

    <c-modal-body *ngIf="selectedOrder() as ord">
      <!-- Summary + Payment Details -->
      <c-row class="mb-4">
        <c-col md="6">
          <h6 class="mb-3">{{ 'my_orders_order_information' | translate }}</h6>
          <table class="table table-sm table-borderless mb-0">
            <tbody>
              <tr>
                <td class="fw-bold">{{ 'my_orders_order_id' | translate }}:</td>
                <td>#{{ ord.id }}</td>
              </tr>
              <tr>
                <td class="fw-bold">{{ 'my_orders_status' | translate }}:</td>
                <td>
                  <c-badge [color]="getStatusBadgeColor(ord.status)">
                    {{ ord.status }}
                  </c-badge>
                </td>
              </tr>
              <tr>
                <td class="fw-bold">{{ 'my_orders_payment_method' | translate }}:</td>
                <td>{{ ord.payment_method }}</td>
              </tr>
              <tr>
                <td class="fw-bold">{{ 'my_orders_total_price' | translate }}:</td>
                <td>{{ ord.total_price }}</td>
              </tr>
              @if (hasPaymentCosts()) {
                <tr>
                  <td class="fw-bold">{{ 'my_orders_payment_costs' | translate }}:</td>
                  <td>{{ ord.payment_costs }}</td>
                </tr>
                <tr>
                  <td class="fw-bold fs-5">{{ 'my_orders_grand_total' | translate }}:</td>
                  <td class="fs-5 fw-bold text-primary">{{ ord.grand_total || 'N/A' }}</td>
                </tr>
              }
              <tr>
                <td class="fw-bold">{{ 'my_orders_order_date' | translate }}:</td>
                <td>{{ ord.created_at }}</td>
              </tr>
              <tr>
                <td class="fw-bold">{{ 'my_orders_last_updated' | translate }}:</td>
                <td>{{ ord.updated_at }}</td>
              </tr>
            </tbody>
          </table>
        </c-col>

        <c-col md="6">
          <!-- Payment-specific details -->
          @if (ord.payment_method === 'web3_payment' && ord.web3_transaction_hash) {
            <h6 class="mb-3">{{ 'my_orders_web3_payment_details' | translate }}</h6>
            <table class="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td class="fw-bold">{{ 'my_orders_transaction_hash' | translate }}:</td>
                  <td class="font-monospace small">
                    <div class="d-flex align-items-center gap-2">
                      <span class="text-break">{{ ord.web3_transaction_hash }}</span>
                      <button
                        cButton
                        color="link"
                        size="sm"
                        class="p-0"
                        (click)="copyToClipboard(ord.web3_transaction_hash)"
                      >
                        <svg cIcon name="cilCopy" size="sm"></svg>
                      </button>
                      <a
                        [href]="'https://bscscan.com/tx/' + ord.web3_transaction_hash"
                        target="_blank"
                        rel="noopener noreferrer"
                        cButton
                        color="link"
                        size="sm"
                        class="p-0"
                      >
                        <svg cIcon name="cilExternalLink" size="sm"></svg>
                      </a>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="fw-bold">{{ 'my_orders_confirmations' | translate }}:</td>
                  <td>
                    <c-badge [color]="(ord.web3_confirmations ?? 0) >= 12 ? 'success' : 'warning'">
                      {{ ord.web3_confirmations ?? 0 }} / 12
                    </c-badge>
                  </td>
                </tr>
                @if (ord.web3_verified_at) {
                  <tr>
                    <td class="fw-bold">{{ 'my_orders_verified_at' | translate }}:</td>
                    <td>{{ ord.web3_verified_at }}</td>
                  </tr>
                }
              </tbody>
            </table>
          }

          @if (ord.payment_method === 'ewallet') {
            <h6 class="mb-3">{{ 'my_orders_ewallet_payment_details' | translate }}</h6>
            <p class="text-body-secondary mb-0">
              <svg cIcon name="cilInfo" size="sm" class="me-1"></svg>
              {{ 'my_orders_ewallet_message' | translate }}
            </p>
          }

          @if (ord.payment_method === 'credit_card') {
            <h6 class="mb-3">{{ 'my_orders_credit_card_payment_details' | translate }}</h6>
            <p class="text-body-secondary mb-0">
              <svg cIcon name="cilInfo" size="sm" class="me-1"></svg>
              {{ 'my_orders_stripe_message' | translate }}
            </p>
          }
        </c-col>
      </c-row>

      <!-- Order Items -->
      <h6 class="mb-3">{{ 'my_orders_order_items' | translate }}</h6>
      <div class="table-responsive">
        <table class="table table-striped table-bordered mb-0">
          <thead>
            <tr>
              <th>{{ 'my_orders_product' | translate }}</th>
              <th class="text-center">{{ 'my_orders_quantity' | translate }}</th>
              <th class="text-end">{{ 'my_orders_unit_price' | translate }}</th>
              <th class="text-end">{{ 'my_orders_subtotal' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (item of ord.order_details || []; track item.id) {
              <tr>
                <td>{{ item.product_name }}</td>
                <td class="text-center">{{ item.quantity }}</td>
                <td class="text-end">{{ item.price }}</td>
                <td class="text-end">{{ item.subtotal }}</td>
              </tr>
            }
          </tbody>
          <tfoot class="table-secondary">
            <tr cTableColor="info">
              <td colspan="3" class="text-end fw-bold">{{ 'my_orders_total_price' | translate }}:</td>
              <td class="text-end fw-bold">{{ ord.total_price }}</td>
            </tr>
            @if (ord.payment_costs && parseFloat(ord.payment_costs) > 0) {
              <tr cTableColor="info">
                <td colspan="3" class="text-end fw-bold">{{ 'my_orders_payment_costs' | translate }}:</td>
                <td class="text-end fw-bold">{{ ord.payment_costs }}</td>
              </tr>
              <tr cTableColor="info">
                <td colspan="3" class="text-end fw-bold fs-5">{{ 'my_orders_grand_total' | translate }}:</td>
                <td class="text-end fw-bold fs-5 text-primary">{{ ord.grand_total || 'N/A' }}</td>
              </tr>
            }
          </tfoot>
        </table>
      </div>
    </c-modal-body>

    <c-modal-footer>
      <div class="d-flex justify-content-between w-100">
        <div class="d-flex gap-2">
          @if (selectedOrder()?.status?.toLowerCase() === 'completed') {
            <button
              cButton
              color="primary"
              (click)="downloadBill(selectedOrder()!.id)"
            >
              <svg cIcon name="cilCloudDownload" size="sm"></svg>
              {{ 'my_orders_download_bill' | translate }}
            </button>

            <button
              cButton
              color="info"
              variant="outline"
              (click)="previewBill(selectedOrder()!.id)"
            >
              <svg cIcon name="cilFile" size="sm"></svg>
              {{ 'my_orders_preview_bill' | translate }}
            </button>
          }
        </div>

        <button cButton color="secondary" (click)="closeOrderDetails()">
          {{ 'my_orders_close' | translate }}
        </button>
      </div>
    </c-modal-footer>
  </c-modal>

  <!-- Bill Preview Modal -->
  <c-modal
    [visible]="billPreviewModalVisible()"
    (visibleChange)="billPreviewModalVisible.set($event)"
    backdrop="static"
    size="xl"
    scrollable="true"
  >
    <c-modal-header class="d-flex justify-content-between align-items-center">
      <h5 cModalTitle class="mb-0">{{ 'my_orders_bill_preview' | translate }}</h5>
      <button
        type="button"
        cButtonClose
        [attr.aria-label]="'my_orders_close' | translate"
        (click)="closeBillPreview()"
      >
      </button>
    </c-modal-header>

    <c-modal-body class="p-0">
      @if (billPreviewUrl()) {
        <iframe
          [src]="billPreviewUrl()"
          style="width: 100%; height: 80vh; border: none;"
          [title]="'my_orders_bill_preview' | translate"
        ></iframe>
      }
    </c-modal-body>

    <c-modal-footer>
      <button cButton color="secondary" (click)="closeBillPreview()">
        {{ 'my_orders_close' | translate }}
      </button>
    </c-modal-footer>
  </c-modal>
</c-container>